import { Call as TwilioCall, Device } from "@twilio/voice-sdk";

import { t } from "@/i18n";
import type { DeviceCallState } from "./state";

export type DeviceOptions = {
  fetchToken: () => Promise<string>;
  onCallState: (state: DeviceCallState) => void;
  onError: (message: string) => void;
  /** Injectable for tests; the real SDK needs a browser with audio hardware. */
  createDevice?: (token: string) => Device;
};

/**
 * Twilio errors the agent can do something about. Everything else gets the
 * generic line — a seller cannot act on "31005 ConnectionError" and telling
 * them the number is how support tickets start with the wrong question.
 */
const MESSAGE_KEYS: Record<number, string> = {
  31401: "softphone.micDenied",
  31402: "softphone.micMissing",
  31000: "softphone.connectionLost",
  31005: "softphone.connectionLost",
  20101: "softphone.sessionExpired",
  31201: "softphone.micUnusable",
};

function messageFor(error: { code?: number; message?: string }): string {
  const key = error.code === undefined ? undefined : MESSAGE_KEYS[error.code];
  return t(key ?? "softphone.generic");
}

const INPUT_KEY = "crm26.softphoneInput";

/**
 * The microphone this agent talks into, remembered per browser.
 *
 * Remembered because a headset that is already plugged in when the CRM opens
 * never *arrives*, so there is nothing to follow; without this it would be
 * picked up once and then lost at the next reload. Guarded both ways because
 * `localStorage` throws in some privacy modes rather than returning null.
 */
function readInput(): string | null {
  try {
    return localStorage.getItem(INPUT_KEY);
  } catch {
    return null;
  }
}

function writeInput(deviceId: string | null): void {
  try {
    if (deviceId) localStorage.setItem(INPUT_KEY, deviceId);
    else localStorage.removeItem(INPUT_KEY);
  } catch {
    // Forgotten at the next reload; still used for this session.
  }
}

async function inputIds(): Promise<Set<string>> {
  const devices = await navigator.mediaDevices.enumerateDevices();
  return new Set(
    devices.filter((device) => device.kind === "audioinput").map((d) => d.deviceId),
  );
}

/**
 * The Twilio `Device`, and the one active browser leg.
 *
 * Wrapped rather than used directly so the provider never touches the SDK: the
 * lifecycle rules here (construct on a gesture, refresh the token before it
 * expires, tear down exactly once) are easy to get subtly wrong and impossible
 * to test through a React component.
 */
export class SoftphoneDevice {
  private device: Device | null = null;
  private call: TwilioCall | null = null;
  private readonly createDevice: (token: string) => Device;
  /** `null` is the operating system's default, whatever that is today. */
  private input: string | null = readInput();
  /** Microphones present when we last looked; a new one is a headset arriving. */
  private knownInputs = new Set<string>();

  constructor(private readonly options: DeviceOptions) {
    this.createDevice =
      options.createDevice ??
      ((token) =>
        new Device(token, {
          // Opus first for quality, PCMU as the fallback every carrier speaks.
          codecPreferences: [TwilioCall.Codec.Opus, TwilioCall.Codec.PCMU],
          logLevel: "warn",
        }));
  }

  get isReady(): boolean {
    return this.device !== null;
  }

  /** Whether there is a browser leg to hang up. */
  get hasCall(): boolean {
    return this.call !== null;
  }

  /** The microphone calls will use, or `null` for the system default. */
  get inputDevice(): string | null {
    return this.input;
  }

  /**
   * Build the `Device`, once, **from a user gesture**.
   *
   * Two browser policies make this non-negotiable and both fail silently.
   * Chrome suspends an `AudioContext` created outside a gesture, so the call
   * connects with no audio and no error at all. And `getUserMedia` outside a
   * gesture is auto-denied by permission policies without ever showing the
   * agent a prompt, so the softphone simply appears broken.
   */
  async ensureReady(): Promise<void> {
    if (this.device) return;

    // Ask for the microphone ourselves rather than letting the SDK do it on
    // first connect: here the failure is a sentence the agent can act on,
    // there it is a call that rings and then dies.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // We only wanted the permission. Holding the tracks open would leave the
      // recording indicator lit in the tab for the rest of the day.
      for (const track of stream.getTracks()) track.stop();
    } catch {
      const message = t("softphone.micDenied");
      this.options.onError(message);
      throw new Error(message);
    }

    // Before the `Device` exists, so its own first enumeration — which reports
    // every microphone as new — is not mistaken for a headset being plugged in.
    this.knownInputs = await inputIds();

    const device = this.createDevice(await this.options.fetchToken());

    device.audio?.on("deviceChange", () => this.followNewInput());

    device.on("error", (error: { code?: number; message?: string }) => {
      this.options.onError(messageFor(error));
    });

    // Tokens live an hour; a seller's day does not end when one expires. The
    // SDK fires this ahead of the deadline precisely so the swap is invisible.
    device.on("tokenWillExpire", () => {
      void this.options
        .fetchToken()
        .then((token) => device.updateToken(token))
        .catch(() => {
          this.options.onError(t("softphone.refreshFailed"));
        });
    });

    this.device = device;
  }

  /**
   * Place the browser leg.
   *
   * `CallRef` is a custom parameter, and it is how `OutboundVoiceView` finds
   * the row this belongs to before Twilio has told us anything about it.
   */
  async connect(ref: string): Promise<void> {
    if (!this.device) throw new Error(t("softphone.notReady"));
    this.options.onCallState("connecting");

    await this.applyInput();
    const call = await this.device.connect({ params: { CallRef: ref } });
    this.call = call;

    // Only fires because `client_leg_twiml` dials with `answerOnBridge`;
    // without it the SDK would jump straight to `accept` and the agent would
    // be told the client answered while their phone was still ringing.
    call.on("ringing", () => this.options.onCallState("ringing"));
    call.on("accept", () => this.options.onCallState("open"));
    call.on("disconnect", () => this.finish());
    call.on("cancel", () => this.finish());
    call.on("reject", () => this.finish());
    call.on("error", (error: { code?: number; message?: string }) => {
      this.options.onError(messageFor(error));
      this.finish();
    });
  }

  disconnect(): void {
    this.call?.disconnect();
  }

  mute(muted: boolean): void {
    this.call?.mute(muted);
  }

  /**
   * Press keys on the call in progress: an extension, a menu option.
   *
   * The SDK plays the tone to the agent and sends it down the line, so the
   * key is heard on both ends like one on a handset. It throws on anything a
   * keypad does not have, hence the check — a stray character must not take
   * the call down with it.
   */
  sendDigits(digits: string): void {
    if (!/^[0-9*#]+$/.test(digits)) return;
    this.call?.sendDigits(digits);
  }

  /**
   * Talk into this microphone from now on, including on the call in progress.
   * `null` goes back to the system default from the next call.
   *
   * Between calls only the choice changes. Opening the microphone now would
   * light the tab's recording indicator until the next call ends.
   */
  chooseInput(deviceId: string | null): void {
    this.input = deviceId;
    writeInput(deviceId);
    if (this.call && deviceId) void this.applyInput();
  }

  /** Tear down for good. After this the object is inert and `ensureReady` rebuilds. */
  destroy(): void {
    this.call?.disconnect();
    this.call = null;
    this.device?.destroy();
    this.device = null;
    this.options.onCallState("idle");
  }

  private finish(): void {
    this.call = null;
    // `setInputDevice` holds the microphone until told otherwise, and a live
    // track keeps the recording indicator lit for the rest of the day.
    void this.device?.audio?.unsetInputDevice().catch(() => undefined);
    this.options.onCallState("idle");
  }

  /**
   * A microphone that was not there a moment ago is a headset being plugged
   * in, and the agent means to talk into it.
   *
   * Not left to the operating system. Windows keeps the laptop's microphone as
   * its default when a USB headset arrives, and Firefox and Safari have no
   * "default" device for the SDK to follow at all — the client ends up hearing
   * the agent through a microphone at arm's length. Listening to the SDK's
   * event rather than the browser's, because `setInputDevice` refuses an id
   * the SDK has not enumerated yet.
   */
  private followNewInput(): void {
    const audio = this.device?.audio;
    if (!audio) return;
    const present = new Set(audio.availableInputDevices.keys());
    const arrived = [...present].find((id) => !this.knownInputs.has(id));
    this.knownInputs = present;
    if (arrived) this.chooseInput(arrived);
  }

  /**
   * Point the SDK at the chosen microphone. An unplugged choice is skipped,
   * not cleared: plugging it back in should not mean choosing it again.
   *
   * Only the microphone. The client's voice stays on the SDK's "default"
   * output, which is the same one "Probar bocinas" plays through, so what the
   * agent hears on the test page is what they will hear on a call. Matching the
   * earpiece by `groupId` looks tidier and is not reliable: Chrome can group a
   * headset's microphone with the laptop's own speakers, and the client then
   * comes out of those while the test tone played in the headset.
   */
  private async applyInput(): Promise<void> {
    const audio = this.device?.audio;
    const chosen = this.input && audio?.availableInputDevices.get(this.input);
    if (!audio || !chosen) return;
    try {
      await audio.setInputDevice(chosen.deviceId);
    } catch {
      // Unplugged between the check and the `getUserMedia`. The default is
      // still a working microphone; failing the call over this would not be.
    }
  }
}
